import { useCallback, useEffect, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { useT } from "@/i18n";

export function useApi(path, deps = [], pollMs = 0) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const reload = useCallback(async () => {
    if (!path) return;
    setLoading(true);
    try { const r = await api.get(path); setData(r.data); setError(null); } catch (e) { setError(errMsg(e)); } finally { setLoading(false); }
  }, [path]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { reload(); if (pollMs) { const t = setInterval(reload, pollMs); return () => clearInterval(t); } }, [reload, ...deps]);
  return { data, loading, error, reload, setData };
}

export const fmtDate = (v) => (v ? new Date(v).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "—");
export const fmtTime = (v) => (v ? new Date(v).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—");
export const fmtDay = (v) => (v ? new Date(v).toLocaleDateString(undefined, { dateStyle: "medium" }) : "—");
export const fmtBytes = (b = 0) => (b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : b > 1024 ? `${(b / 1024).toFixed(0)} KB` : `${b} B`);

export function PageHeader({ eyebrow, title, subtitle, actions, testId }) {
  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between mb-6 rise" data-testid={testId}>
      <div>
        {eyebrow && <div className="eyebrow mb-1">{eyebrow}</div>}
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">{title}</h1>
        {subtitle && <p className="text-sm text-muted-foreground mt-1 max-w-2xl">{subtitle}</p>}
      </div>
      {actions && <div className="flex gap-2 flex-wrap">{actions}</div>}
    </div>
  );
}

export function StatCard({ label, value, hint, icon: Icon, tone = "default", testId }) {
  const tones = { default: "text-slate-900", accent: "text-blue-600", teal: "text-teal-600", warn: "text-amber-600", danger: "text-red-600" };
  return (
    <div className="bg-card border rounded-lg p-4 flex flex-col gap-2 rise" data-testid={testId}>
      <div className="flex items-center justify-between"><span className="eyebrow">{label}</span>{Icon && <Icon className="h-4 w-4 text-muted-foreground" />}</div>
      <div className={`text-2xl sm:text-3xl font-semibold font-heading tabular-nums ${tones[tone]}`}>{value ?? "—"}</div>
      {hint && <div className="text-xs text-muted-foreground">{hint}</div>}
    </div>
  );
}

const STATUS_STYLES = {
  active: "bg-emerald-50 text-emerald-700 border-emerald-200", lifetime: "bg-emerald-50 text-emerald-700 border-emerald-200",
  trial: "bg-blue-50 text-blue-700 border-blue-200", gift: "bg-violet-50 text-violet-700 border-violet-200", promotional: "bg-violet-50 text-violet-700 border-violet-200",
  grace: "bg-amber-50 text-amber-700 border-amber-200", expired: "bg-red-50 text-red-700 border-red-200", suspended: "bg-red-50 text-red-700 border-red-200",
  cancelled: "bg-slate-100 text-slate-600 border-slate-200", INFO: "bg-slate-100 text-slate-600 border-slate-200", WARN: "bg-amber-50 text-amber-700 border-amber-200",
  SECURITY: "bg-blue-50 text-blue-700 border-blue-200", CRITICAL: "bg-red-50 text-red-700 border-red-200", inactive: "bg-slate-100 text-slate-500 border-slate-200",
};
export function StatusBadge({ status, testId }) {
  return <Badge variant="outline" className={`font-mono text-[10px] uppercase tracking-wider ${STATUS_STYLES[status] || "bg-slate-100 text-slate-600"}`} data-testid={testId}>{status || "—"}</Badge>;
}

export function EmptyState({ title, hint }) {
  return <div className="border border-dashed rounded-lg p-10 text-center text-sm text-muted-foreground bg-card/50"><div className="font-medium text-foreground mb-1">{title}</div>{hint}</div>;
}

export function Field({ label, children, hint }) {
  return <div className="space-y-1.5"><Label className="text-xs font-medium text-slate-600">{label}</Label>{children}{hint && <p className="text-[11px] text-muted-foreground">{hint}</p>}</div>;
}

export function PrivilegedDialog({ open, onOpenChange, title, description, onConfirm, children, busy, confirmLabel, destructive }) {
  const { t } = useT();
  const [reason, setReason] = useState("");
  const [password, setPassword] = useState("");
  useEffect(() => { if (!open) { setReason(""); setPassword(""); } }, [open]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card sm:max-w-lg" data-testid="privileged-dialog">
        <DialogHeader><DialogTitle className="font-heading">{title}</DialogTitle>{description && <p className="text-sm text-muted-foreground">{description}</p>}</DialogHeader>
        <div className="space-y-4">
          {children}
          <Field label={t("common.reason")}><Textarea data-testid="privileged-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={2} /></Field>
          <Field label={t("common.confirm_password")}><Input data-testid="privileged-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} /></Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t("common.cancel")}</Button>
          <Button data-testid="privileged-confirm" variant={destructive ? "destructive" : "default"} disabled={busy || reason.length < 3 || !password} onClick={() => onConfirm({ reason, confirm_password: password })}>{confirmLabel || t("common.confirm")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function JsonBlock({ value }) {
  return <pre className="text-[11px] font-mono bg-slate-950 text-slate-100 rounded-md p-3 overflow-auto max-h-64">{JSON.stringify(value ?? null, null, 2)}</pre>;
}
